import React, { useEffect, useState } from 'react'
import { useTranslation } from '@sailor/i18n-web'
import { useRequest } from 'ahooks'
import { Button, Input, Radio, Tooltip, FormPro } from '@roo/roo'
import Selector from '@roo/roo/Selector'
import Modal from '@roo/roo/Modal'
import { adjustBalance } from '@/pages/balance/utils/api'
import { IBalanceItem, IAdjustBalanceParams } from '@/pages/balance/utils/types'
import { AdjustType } from '@/pages/balance/utils/constants'
import { failToast, successToast } from '@/common/utils/toast'
import { isZh, amountToMinorUnit, getAmountFractionPlaces } from '@/common/utils/i18nFuncs'
import styles from './index.module.less'

interface BalanceAdjustProps {
  record: IBalanceItem
  /** 津贴制度业务编号 */
  budgetPolicyNo: string
  onSuccess?: () => void
}

/** 过滤输入：只保留数字和最多 N 位小数（N 由当前区域货币决定） */
const filterAmountInput = (value: string): string => {
  const fractionPlaces = getAmountFractionPlaces()
  let val = value.replace(/[^\d.]/g, '')
  const dotIndex = val.indexOf('.')
  if (dotIndex !== -1) {
    val = val.slice(0, dotIndex + 1) + val.slice(dotIndex + 1).replace(/\./g, '')
    const parts = val.split('.')
    if (parts[1] && parts[1].length > fractionPlaces) {
      val = parts[0] + '.' + parts[1].slice(0, fractionPlaces)
    }
  }
  return val
}

interface BalanceAdjustModalProps extends BalanceAdjustProps {
  onClose: () => void
}

const BalanceAdjustModal: React.FC<BalanceAdjustModalProps> = ({ record, budgetPolicyNo, onSuccess, onClose }) => {
  const { t } = useTranslation()
  const [form] = FormPro.useForm()
  const [adjustType, setAdjustType] = useState<AdjustType>(AdjustType.TARGET_AMOUNT)
  const [deltaDirection, setDeltaDirection] = useState<'inc' | 'dec'>('inc')
  const [deltaAmountInput, setDeltaAmountInput] = useState<string>('')
  const [deltaAmountError, setDeltaAmountError] = useState<string>('')

  const currency = record.totalAvailableAmount?.currency || ''
  const containerRef = React.useRef<HTMLDivElement | null>(null)

  const { loading, run: submitAdjust } = useRequest(
    (params: IAdjustBalanceParams) => adjustBalance(params),
    {
      manual: true,
      onSuccess: () => {
        successToast(t('e_pc_msg_adjust_success', '余额调整成功'))
        onClose()
        onSuccess?.()
      },
      onError: (error: any) => {
        const msg = error?.message || t('e_pc_msg_adjust_fail_insufficient', '调整失败，请刷新余额后重试')
        failToast(msg)
      },
    }
  )

  /** 当前有效余额（分） */
  const availableAmountCents = parseInt(record.totalAvailableAmount?.amount || '0', 10)

  /** 纯函数校验：外部传入当前方向，避免闭包陈旧问题 */
  const checkDeltaAmount = (value: string, direction: 'inc' | 'dec'): string => {
    const fractionPlaces = getAmountFractionPlaces()
    if (!value || !value.trim()) {
      return t('e_pc_placeholder_after_adjust', '请输入金额')
    }
    if (!new RegExp(`^(?!0\\d)\\d+(\\.\\d{1,${fractionPlaces}})?$`).test(value.trim()) || Number(value) <= 0) {
      return t('e_pc_maximum_decimal_places', { pluralCount: fractionPlaces, number: fractionPlaces }) || `请输入大于0的整数或小数，最多${fractionPlaces}位小数`
    }
    if (direction === 'dec') {
      const inputMinorUnit = amountToMinorUnit(value)
      if (inputMinorUnit > availableAmountCents) {
        return t('e_pc_msg_dec_exceed_balance', '扣减金额大于余额')
      }
    }
    return ''
  }

  /** TARGET 模式金额校验（供 FormPro rules 使用，无外部 state 依赖） */
  const validateTargetAmount = (_: any, value: string) => {
    const fractionPlaces = getAmountFractionPlaces()
    if (!value || !value.trim()) {
      return Promise.reject(new Error(t('e_pc_placeholder_after_adjust', '请输入金额')))
    }
    if (!new RegExp(`^(?!0\\d)\\d+(\\.\\d{1,${fractionPlaces}})?$`).test(value.trim()) || Number(value) <= 0) {
      return Promise.reject(new Error(t('e_pc_maximum_decimal_places', { pluralCount: fractionPlaces, number: fractionPlaces }) || `请输入大于0的整数或小数，最多${fractionPlaces}位小数`))
    }
    return Promise.resolve()
  }

  const handleSubmit = () => {
    // DELTA 模式：手动校验增减金额
    if (adjustType === AdjustType.DELTA_AMOUNT) {
      const err = checkDeltaAmount(deltaAmountInput, deltaDirection)
      setDeltaAmountError(err)
      if (err) return
    }
    form.validateFields().then((values) => {
      Modal.confirm({
        title: t('e_pc_confirm_adjust', '确认调整余额？'),
        status: 'question',
        cancelText: t('e_pc_cancel', '取消'),
        confirmText: t('e_pc_confirm_button', '确定'),
        cancelButtonProps: { type: 'hollow', shape: 'round', style: { border: '1px solid #d9d9d9', borderRadius: 999 } },
        onConfirm: () => {
          let adjustAmount: number
          if (adjustType === AdjustType.TARGET_AMOUNT) {
            adjustAmount = amountToMinorUnit(values.targetAmount)
          } else {
            const abs = amountToMinorUnit(deltaAmountInput)
            adjustAmount = deltaDirection === 'inc' ? abs : -abs
          }

          submitAdjust({
            budgetFeeId: record.budgetFeeId,
            budgetPolicyNo,
            budgetFeeVersion: record.budgetFeeVersion,
            adjustType,
            adjustAmount,
            reason: values.reason || undefined,
          })
        },
      })
    })
      .catch(() => {
      // 校验失败，表单已自动展示红色提示，无需额外处理
      })
  }

  // deltaDirection 变化后，若已有输入则重新校验（此时 state 已是最新值）
  useEffect(() => {
    if (deltaAmountInput) {
      setDeltaAmountError(checkDeltaAmount(deltaAmountInput, deltaDirection))
    }
  }, [deltaDirection])

  const deltaDirectionOptions = [
    { label: t('e_pc_action_inc', '增加'), value: 'inc' },
    { label: t('e_pc_action_dec', '减少'), value: 'dec' },
  ]

  return (
    <Modal
      visible
      title={t('e_pc_balance_adjust', '余额调整')}
      style={{ width: '580px' }}
      onClose={onClose}
      footer={
        <div className={styles.modal_footer}>
          <Button type="hollow" shape="round" style={{ border: '1px solid #d9d9d9', borderRadius: 999 }} onClick={onClose}>{t('e_pc_cancel', '取消')}</Button>
          <Button type="brand" loading={loading} onClick={handleSubmit}>
            {t('e_pc_confirm_button', '确定')}
          </Button>
        </div>
      }
    >
      <div ref={containerRef} className={styles.modal_body}>
        <FormPro
          form={form}
          layout="horizontal"
          colon={false}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 8 }}
        >
          {/* 员工账号 */}
          {record.email && (
            <FormPro.Item label={t('e_pc_account_id', '账号')}>
              <span className={styles.staff_account}>{record.email}</span>
            </FormPro.Item>
          )}

          {/* 调整方式 */}
          <FormPro.Item label={t('e_pc_adjust_method', '调整方式')}>
            <Radio.Group
              value={adjustType}
              className={styles.radio_group_vertical}
              onChange={(val: AdjustType) => {
                setAdjustType(val)
                form.resetFields(['targetAmount', 'deltaAmount'])
                setDeltaAmountInput('')
              }}
            >
              <Radio value={AdjustType.TARGET_AMOUNT}>
                {t('e_pc_placeholder_after_adjust', '输入调整后金额')}
              </Radio>
              <Radio value={AdjustType.DELTA_AMOUNT}>
                {t('e_pc_placeholder_inc_dec', '输入增减金额')}
              </Radio>
            </Radio.Group>
          </FormPro.Item>

          {/* TARGET 模式：调整后金额 */}
          {adjustType === AdjustType.TARGET_AMOUNT && (
            <FormPro.Item
              label={t('e_pc_after_adjust_amount', '调整后金额')}
              name="targetAmount"
              required
              normalize={filterAmountInput}
              rules={[{ validator: validateTargetAmount }]}
            >
              <Input
                placeholder={t('e_pc_placeholder_after_adjust', '请输入')}
                suffix={<span className={styles.unit}>{currency}</span>}
              />
            </FormPro.Item>
          )}

          {/* DELTA 模式：增减金额（手动管理错误，避免 validator 闭包陈旧问题） */}
          {adjustType === AdjustType.DELTA_AMOUNT && (
            <FormPro.Item
              label={t('e_pc_inc_dec_amount', '增减金额')}
              required
              validateStatus={deltaAmountError ? 'error' : ''}
              help={deltaAmountError || undefined}
            >
              <div className={styles.prefix_input}>
                <Selector
                  value={deltaDirection}
                  options={deltaDirectionOptions}
                  onChange={(val: 'inc' | 'dec') => setDeltaDirection(val)}
                  className={styles.direction_select}
                  style={{ width: isZh() ? 80 : 110 }}
                  popupContainer={() => containerRef.current || document.body}
                  showSelectIcon={false}
                  popupStyle={{ width: isZh() ? 80 : 110, minWidth: isZh() ? 80 : 110, overflow: 'hidden' }}
                />
                <Input
                  placeholder={t('e_pc_placeholder_inc_dec', '请输入')}
                  className={styles.delta_input}
                  suffix={<span className={styles.unit}>{currency}</span>}
                  value={deltaAmountInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const filtered = filterAmountInput(e.target.value)
                    setDeltaAmountInput(filtered)
                    // 有内容时实时校验；清空时重置错误
                    setDeltaAmountError(filtered ? checkDeltaAmount(filtered, deltaDirection) : '')
                  }}
                />
              </div>
            </FormPro.Item>
          )}

          {/* 调整原因 */}
          <FormPro.Item label={t('e_pc_adjust_reason', '调整原因')} name="reason">
            <Input.Textarea
              placeholder={t('e_pc_placeholder_reason_opt', '请输入调整原因（选填）')}
              rows={2}
              style={{ resize: 'none' }}
            />
          </FormPro.Item>
        </FormPro>
      </div>
    </Modal>
  )
}

/** 判断津贴当前的有效状态：active=生效中，notStarted=未生效，expired=已失效 */
const getBalanceStatus = (record: IBalanceItem): 'active' | 'notStarted' | 'expired' => {
  const now = Date.now()
  const start = record.feeStartTime ?? 0
  const end = record.feeEndTime ?? 0
  // periodType=1 长期有效，无需判断结束时间
  if (record.periodType === 1) {
    return now >= start ? 'active' : 'notStarted'
  }
  if (start > 0 && now < start) return 'notStarted'
  if (end > 0 && now > end) return 'expired'
  return 'active'
}

const BalanceAdjust: React.FC<BalanceAdjustProps> = ({ record, budgetPolicyNo, onSuccess }) => {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  const status = getBalanceStatus(record)
  const active = status === 'active'

  let disabledTooltip = ''
  if (status === 'notStarted') {
    disabledTooltip = t('e_pc_balance_management_ban', '津贴未生效，余额管理暂不可用')
  } else if (status === 'expired') {
    disabledTooltip = t('e_pc_balance_expired_not_adjusted', '余额已失效，无法调整')
  }

  const adjustBtn = (
    <Button
      type="brand-text"
      disabled={!active}
      onClick={active ? () => setVisible(true) : undefined}
    >
      {t('e_pc_balance_adjust', '余额调整')}
    </Button>
  )

  return (
    <>
      {active ? (
        adjustBtn
      ) : (
        <Tooltip content={disabledTooltip} placement="top">
          <span style={{ cursor: 'pointer' }}>{adjustBtn}</span>
        </Tooltip>
      )}
      {visible && (
        <BalanceAdjustModal
          record={record}
          budgetPolicyNo={budgetPolicyNo}
          onSuccess={onSuccess}
          onClose={() => setVisible(false)}
        />
      )}
    </>
  )
}

export default BalanceAdjust
